/**
 * 服务任务属性组件
 * 包含服务类型选择、服务配置等功能
 */
import React from 'react';
import { Form, Select, Input, Card, Space, Button } from 'antd';
import { ApiOutlined, SettingOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const ServiceTaskProperties = ({ form }) => {
  const serviceType = Form.useWatch('serviceType', form) || 'http';
  const serviceConfig = Form.useWatch('serviceConfig', form) || {};

  // 服务类型选项
  const serviceTypeOptions = [
    { value: 'http', label: 'HTTP服务调用' },
    { value: 'rpc', label: 'RPC服务调用' },
    { value: 'database', label: '数据库操作' },
    { value: 'message', label: '消息队列' },
    { value: 'custom', label: '自定义服务' }
  ];

  // 渲染HTTP服务配置
  const renderHttpConfig = () => (
    <div className="space-y-3">
      <Form.Item
        label="请求方法"
        name={['serviceConfig', 'method']}
        className="mb-3"
        initialValue="GET"
      >
        <Select>
          <Option value="GET">GET</Option>
          <Option value="POST">POST</Option>
          <Option value="PUT">PUT</Option>
          <Option value="DELETE">DELETE</Option>
          <Option value="PATCH">PATCH</Option>
        </Select>
      </Form.Item>
      
      <Form.Item
        label="请求URL"
        name={['serviceConfig', 'url']}
        className="mb-3"
        rules={[{ required: true, message: '请输入请求URL' }]}
      >
        <Input placeholder="https://api.example.com/endpoint" />
      </Form.Item>
      
      <Form.Item
        label="请求头"
        name={['serviceConfig', 'headers']}
        className="mb-3"
      >
        <TextArea 
          rows={3} 
          placeholder={`{\n  "Content-Type": "application/json",\n  "Authorization": "Bearer token"\n}`}
        />
      </Form.Item>
      
      <Form.Item
        label="请求体"
        name={['serviceConfig', 'body']}
        className="mb-0"
      >
        <TextArea 
          rows={4} 
          placeholder={`{\n  "key": "value"\n}`}
        />
      </Form.Item>
    </div>
  );

  // 渲染RPC服务配置
  const renderRpcConfig = () => (
    <div className="space-y-3">
      <Form.Item
        label="服务地址"
        name={['serviceConfig', 'address']}
        className="mb-3"
        rules={[{ required: true, message: '请输入服务地址' }]}
      >
        <Input placeholder="localhost:8080" />
      </Form.Item>
      
      <Form.Item
        label="服务名称"
        name={['serviceConfig', 'serviceName']}
        className="mb-3"
        rules={[{ required: true, message: '请输入服务名称' }]}
      >
        <Input placeholder="UserService" />
      </Form.Item>
      
      <Form.Item
        label="方法名称"
        name={['serviceConfig', 'methodName']}
        className="mb-3"
        rules={[{ required: true, message: '请输入方法名称' }]}
      >
        <Input placeholder="getUserInfo" />
      </Form.Item>
      
      <Form.Item
        label="参数配置"
        name={['serviceConfig', 'parameters']}
        className="mb-0"
      >
        <TextArea 
          rows={4} 
          placeholder={`{\n  "userId": "${'{'}userId{'}'}",\n  "includeDetails": true\n}`}
        />
      </Form.Item>
    </div>
  );

  // 渲染数据库操作配置
  const renderDatabaseConfig = () => (
    <div className="space-y-3">
      <Form.Item
        label="数据源"
        name={['serviceConfig', 'dataSource']}
        className="mb-3"
        rules={[{ required: true, message: '请选择数据源' }]}
      >
        <Select placeholder="选择数据源">
          <Option value="primary">主数据库</Option>
          <Option value="secondary">从数据库</Option>
          <Option value="cache">缓存数据库</Option>
        </Select>
      </Form.Item>
      
      <Form.Item
        label="操作类型"
        name={['serviceConfig', 'operation']}
        className="mb-3"
        initialValue="SELECT"
      >
        <Select>
          <Option value="SELECT">查询 (SELECT)</Option>
          <Option value="INSERT">插入 (INSERT)</Option>
          <Option value="UPDATE">更新 (UPDATE)</Option>
          <Option value="DELETE">删除 (DELETE)</Option>
        </Select>
      </Form.Item>
      
      <Form.Item
        label="SQL语句"
        name={['serviceConfig', 'sql']}
        className="mb-0"
        rules={[{ required: true, message: '请输入SQL语句' }]}
      >
        <TextArea 
          rows={4} 
          placeholder={`SELECT * FROM users WHERE id = ${'${userId}'}`}
        />
      </Form.Item>
    </div>
  );

  // 渲染消息队列配置
  const renderMessageConfig = () => (
    <div className="space-y-3">
      <Form.Item
        label="队列类型"
        name={['serviceConfig', 'queueType']}
        className="mb-3"
        initialValue="rabbitmq"
      >
        <Select>
          <Option value="rabbitmq">RabbitMQ</Option>
          <Option value="kafka">Kafka</Option>
          <Option value="redis">Redis</Option>
          <Option value="activemq">ActiveMQ</Option>
        </Select>
      </Form.Item>
      
      <Form.Item
        label="队列名称"
        name={['serviceConfig', 'queueName']}
        className="mb-3"
        rules={[{ required: true, message: '请输入队列名称' }]}
      >
        <Input placeholder="user.notification.queue" />
      </Form.Item>
      
      <Form.Item
        label="消息内容"
        name={['serviceConfig', 'message']}
        className="mb-0"
      >
        <TextArea 
          rows={4} 
          placeholder={`{\n  "type": "notification",\n  "userId": "${'${userId}'}",\n  "message": "任务已完成"\n}`}
        />
      </Form.Item>
    </div>
  );

  // 渲染自定义服务配置
  const renderCustomConfig = () => (
    <div className="space-y-3">
      <Form.Item
        label="服务类名"
        name={['serviceConfig', 'className']}
        className="mb-3"
        rules={[{ required: true, message: '请输入服务类名' }]}
      >
        <Input placeholder="com.example.service.CustomService" />
      </Form.Item>
      
      <Form.Item
        label="方法名称"
        name={['serviceConfig', 'methodName']}
        className="mb-3"
        rules={[{ required: true, message: '请输入方法名称' }]}
      >
        <Input placeholder="execute" />
      </Form.Item>
      
      <Form.Item
        label="配置参数"
        name={['serviceConfig', 'config']}
        className="mb-0"
      >
        <TextArea 
          rows={4} 
          placeholder={`{\n  "param1": "value1",\n  "param2": "value2"\n}`}
        />
      </Form.Item>
    </div>
  );

  // 根据服务类型渲染对应配置
  const renderServiceConfig = () => {
    switch (serviceType) {
      case 'http':
        return renderHttpConfig();
      case 'rpc':
        return renderRpcConfig();
      case 'database':
        return renderDatabaseConfig();
      case 'message':
        return renderMessageConfig();
      case 'custom':
        return renderCustomConfig();
      default:
        return renderHttpConfig();
    }
  };

  return (
    <Card 
      size="small" 
      className="mb-4"
      title={
        <div className="flex items-center space-x-2">
          <ApiOutlined />
          <span>服务配置</span>
        </div>
      }
    >
      <Form.Item
        label="服务类型"
        name="serviceType"
        className="mb-3"
        initialValue="http"
      >
        <Select placeholder="选择服务类型">
          {serviceTypeOptions.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </Form.Item>
      
      {renderServiceConfig()}
      
      <div className="mt-4 pt-3 border-t border-gray-200">
        <Form.Item
          label="超时时间(秒)"
          name={['serviceConfig', 'timeout']}
          className="mb-3"
          initialValue={30}
        >
          <Input type="number" placeholder="30" min={1} max={300} />
        </Form.Item>
        
        <Form.Item
          label="重试次数"
          name={['serviceConfig', 'retryCount']}
          className="mb-0"
          initialValue={0}
        >
          <Input type="number" placeholder="0" min={0} max={5} />
        </Form.Item>
      </div>
      
      <div className="mt-2 text-xs text-gray-500">
        <div>• 服务调用将在流程执行时进行</div>
        <div>• 请确保服务地址和配置正确</div>
        <div>• 支持使用流程变量作为参数</div>
      </div>
    </Card>
  );
};

export default ServiceTaskProperties;